import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    number: "1",
    title: "Copy the Zoopla URL",
    description:
      "Find your listing on Zoopla and copy the page URL. Sales, lettings, new homes: they all work.",
  },
  {
    number: "2",
    title: "QuiteRight does the rest",
    description:
      "Photos, price, address, bedrooms, features, and description are imported automatically. No copying, no pasting, no spreadsheets.",
  },
  {
    number: "3",
    title: "Choose a template and publish",
    description:
      "Select Prestige, Modern, Classic, or Bold. Your branded video with voiceover and music is ready in 60 seconds. Share to every social platform.",
  },
]

const FEATURES = [
  {
    title: "Automatic Zoopla data import",
    description:
      "Photos, price, address, property type, bedrooms, and key features are extracted directly from the Zoopla listing page. Zero manual input.",
  },
  {
    title: "Works with Zoopla lettings too",
    description:
      "Rental listings on Zoopla work identically to sales. Paste the URL and the script adapts to letting-specific language automatically.",
  },
  {
    title: "Professional AI voiceover",
    description:
      "QuiteRight generates a property-specific script from the Zoopla listing and delivers it as a polished AI voiceover. Choose from multiple voices.",
  },
  {
    title: "Every social format included",
    description:
      "One video, every platform. Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn formats are all generated automatically.",
  },
  {
    title: "Consistent agency branding",
    description:
      "Your logo, colours, and contact details appear on every video. No manual placement, no inconsistency between team members.",
  },
  {
    title: "Ready in 60 seconds",
    description:
      "From pasting the Zoopla URL to a finished, branded video with voiceover and music. Faster than writing a social media caption.",
  },
]

const STATS = [
  { stat: "60s", label: "from Zoopla URL to finished video" },
  { stat: "0", label: "photos to download or upload" },
  { stat: "50", label: "videos per month on the Managed plan" },
  { stat: "4", label: "professional video templates" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Does QuiteRight work with Zoopla listings?",
    a: "Yes. QuiteRight supports Zoopla alongside Rightmove and OnTheMarket. Paste any Zoopla listing URL and QuiteRight automatically imports the photos, price, address, and features to create a branded video ad.",
  },
  {
    q: "Can I create videos for Zoopla rental listings?",
    a: "Absolutely. Zoopla lettings URLs work exactly the same as sales listings. The generated script adapts automatically to rental-specific language, including rent per month, deposit, and availability.",
  },
  {
    q: "Do I need a Zoopla Pro account to use QuiteRight?",
    a: "No. QuiteRight works with the publicly available Zoopla listing page. You just need the URL of the listing you want to turn into a video. No API access or special account is required.",
  },
  {
    q: "Can I add Zoopla videos to my social media?",
    a: "Yes. Every video comes ready for Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. You can download the video or publish directly from QuiteRight if you have connected your social accounts.",
  },
  {
    q: "What if my Zoopla listing has fewer photos?",
    a: "QuiteRight adapts to the available content. Even listings with just 3-4 photos produce a professional video. The script and timing adjust automatically to suit the number of images available.",
  },
  {
    q: "Does QuiteRight also work with Rightmove?",
    a: "Yes. QuiteRight supports all three major UK portals: Rightmove, Zoopla, and OnTheMarket. The process is identical for each. Paste the URL, pick a template, and your video is ready in 60 seconds.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function IntegrationZoopla({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Create Video Ads from Zoopla Listings",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Integrations", path: "/integrations" },
            { name: "Zoopla" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Integration
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Turn Zoopla listings into video ads
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Paste any Zoopla listing URL. QuiteRight imports photos, price,
                and features automatically, then creates a branded video with
                voiceover and music in 60 seconds.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -right-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-right-2">
                <p className="text-xs font-medium text-warm-grey">From Zoopla to video</p>
                <p className="font-display text-xl font-bold text-white">60 seconds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              How it works
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Paste. Pick. Publish
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <ScrollReveal key={step.number}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-red font-display text-lg font-bold text-white">
                    {step.number}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-charcoal">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">
                    {step.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What you get
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Zoopla listing to social video, automated
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {feature.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("integrations/zoopla")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Your Zoopla listings deserve video
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Every listing, every platform, every time. Try QuiteRight free
              for 14 days and see the difference video makes.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
